import {
  ArrowLeft,
  BarChart2,
  ChevronDown,
  CircleStop,
  Download,
  Ellipsis,
  PencilLine,
  SlidersHorizontal,
  Trash2,
} from 'lucide-react';

import {
  CampaignStatus,
  isCampaignDeletable,
  useCampaignOverview,
  type Campaign,
} from '@/entities/campaign';
import { Badge } from '@/shared/components/shadcn/badge';
import { Button } from '@/shared/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/shadcn/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/components/shadcn/popover';

import { requestCampaignExportDialog } from '@/features/campaign/overlays/request-campaign-export-dialog';
import { useDeleteCampaignRequest } from '@/features/campaign/overlays/use-delete-campaign-request';
import { useEndCampaignRequest } from '@/features/campaign/overlays/use-end-campaign-request';
import { useRenameCampaignRequest } from '@/features/campaign/overlays/use-rename-campaign-request';
import { Skeleton } from '@/shared/components/shadcn/skeleton';
import { TooltipFlat } from '@/shared/components/shadcn/tooltip/tooltip-flat';
import { Stage } from '@/shared/components/stage/stage';
import { useWorkspace } from '@/shared/hooks/use-workspace';
import { useNavigate } from '@tanstack/react-router';
import { CampaignKeywordChip } from '../../../campaign-editor/primitives';
import { statusConfigs } from '../constants';
import { exportCampaign } from '../hooks/use-campaign-detail-export';
import { formatCampaignPeriod } from '../utils';

export function CampaignDetailHeader({
  campaignId,
  campaign,
  isLoading,
  onBack,
  onEditSetup,
}: {
  campaignId: string;
  campaign: Campaign | null;
  isLoading: boolean;
  onBack: () => void;
  onEditSetup: () => void;
}) {
  const { workspace } = useWorkspace();
  const navigate = useNavigate();
  const overviewQuery = useCampaignOverview(campaignId);
  const canExport = (overviewQuery.data?.tracked_posts_count ?? 0) > 0;
  const canEditSetup = campaign?.status !== CampaignStatus.ENDED;

  const onDeleteCampaignRequest = useDeleteCampaignRequest({
    onSuccess: () => {
      navigate({
        to: '/workspace/$workspaceId/campaigns/',
        params: { workspaceId: workspace.id },
      });
    },
  });
  const onEndCampaignRequest = useEndCampaignRequest();
  const onRenameCampaignRequest = useRenameCampaignRequest();

  return (
    <header className="border-border bg-background border-b px-8 py-6">
      <div className="space-y-4">
        <Button className="w-fit" variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="mr-2 size-4" />
          Back to campaigns
        </Button>
        <Stage>
          <Stage.Scene status={isLoading} className="flex flex-col gap-4">
            <div className="flex justify-between">
              <Skeleton className="h-10 w-60" />
              <div className="flex gap-2">
                <Skeleton className="h-10 w-24" />
                <Skeleton className="h-10 w-10" />
              </div>
            </div>
            <div className="flex gap-4">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-6 w-20" />
            </div>
          </Stage.Scene>
          <Stage.Scene status={!!campaign} data={campaign}>
            {(campaign) => {
              const statusConfig = statusConfigs.get(campaign.status);
              const handleRequestExportCampaign = async () => {
                if (!campaign) return;
                await requestCampaignExportDialog({
                  canExport: canExport,
                  campaign,
                  onExport: (request) => exportCampaign(campaign.id, campaign.name, request),
                });
              };
              return (
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div className="space-y-3">
                    <h1 className="flex flex-wrap items-center gap-3">
                      <span className="text-foreground min-w-0 truncate text-3xl font-semibold tracking-tight">
                        {campaign.name}
                      </span>
                      <Badge variant={statusConfig.variants} className="shrink-0">
                        {statusConfig.label}
                      </Badge>
                    </h1>

                    <div className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                      <span>{formatCampaignPeriod(campaign.start_date, campaign.end_date)}</span>
                      <div className="border-border h-4 border-l" aria-hidden="true" />
                      <KeywordFiltersPopover keywords={campaign.creator_tracking_keywords || []} />
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-10"
                      onClick={() =>
                        navigate({
                          to: '/workspace/$workspaceId/campaigns/$campaignId/analysis',
                          params: { workspaceId: workspace.id, campaignId },
                        })
                      }
                    >
                      <BarChart2 className="size-4" />
                      Analyze
                    </Button>
                    {canExport ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-10"
                        onClick={handleRequestExportCampaign}
                      >
                        <Download className="size-4" />
                        Export
                      </Button>
                    ) : (
                      <TooltipFlat
                        asChild
                        content="Add campaign posts before exporting performance."
                      >
                        <div tabIndex={0} className="outline-hidden">
                          <Button variant="outline" size="sm" className="h-10" disabled>
                            <Download className="size-4" />
                            Export
                          </Button>
                        </div>
                      </TooltipFlat>
                    )}

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="icon" className="size-10">
                          <Ellipsis className="size-4" />
                          <span className="sr-only">Open campaign actions</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-44">
                        {canEditSetup ? (
                          <DropdownMenuItem onClick={onEditSetup}>
                            <SlidersHorizontal className="size-4" />
                            Edit setting
                          </DropdownMenuItem>
                        ) : (
                          <TooltipFlat
                            asChild
                            side="left"
                            content="Ended campaigns cannot edit settings."
                          >
                            <div tabIndex={0} className="outline-hidden">
                              <DropdownMenuItem disabled>
                                <SlidersHorizontal className="size-4" />
                                Edit setting
                              </DropdownMenuItem>
                            </div>
                          </TooltipFlat>
                        )}
                        <DropdownMenuItem
                          onClick={() => onRenameCampaignRequest(campaign.id, campaign.name)}
                        >
                          <PencilLine className="size-4" />
                          Rename
                        </DropdownMenuItem>
                        {campaign.status !== CampaignStatus.ENDED ? (
                          <DropdownMenuItem
                            onClick={() => onEndCampaignRequest(campaign.id, campaign.name)}
                          >
                            <CircleStop className="size-4" />
                            End campaign
                          </DropdownMenuItem>
                        ) : null}
                        {isCampaignDeletable(campaign.status) ? (
                          <DropdownMenuItem
                            variant="destructive"
                            onClick={() => onDeleteCampaignRequest(campaign.id, campaign.name)}
                          >
                            <Trash2 className="size-4" />
                            Delete
                          </DropdownMenuItem>
                        ) : (
                          <TooltipFlat
                            asChild
                            side="left"
                            content="Active campaigns cannot be deleted."
                          >
                            <div tabIndex={0} className="outline-hidden">
                              <DropdownMenuItem variant="destructive" disabled>
                                <Trash2 className="size-4" />
                                Delete
                              </DropdownMenuItem>
                            </div>
                          </TooltipFlat>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              );
            }}
          </Stage.Scene>
        </Stage>
      </div>
    </header>
  );
}

function KeywordFiltersPopover({ keywords }: { keywords: string[] }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="text-muted-foreground h-7 gap-1.5 px-2 text-sm font-normal"
        >
          Keyword filters ({keywords.length})
          <ChevronDown className="size-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[320px] p-3">
        <p className="text-muted-foreground text-sm">
          Posts are tracked when they match any keyword below.
        </p>
        {keywords.length > 0 ? (
          <div className="mt-3 flex max-h-44 flex-wrap gap-2 overflow-y-auto pr-1">
            {keywords.map((keyword) => (
              <CampaignKeywordChip key={keyword} label={keyword} />
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground mt-3 text-sm">No keyword filters configured.</p>
        )}
      </PopoverContent>
    </Popover>
  );
}
